import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../services/supabaseClient';
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate, markTemplateSent,
} from '../../services/emailTemplateService';
import {
  listSendsByTemplate, listAllSendsWithTemplate, statsByTemplate,
  aggregateStats, groupByRun,
} from '../../services/emailSendsService';

const STORAGE_BUCKET = 'mf-imoveis';
const STORAGE_FOLDER = 'email-assets';
const DEFAULT_FROM = 'MF Real Estate <noreply@abianca.com.br>';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BLAST_ENDPOINT = '/api/mf-email-blast';
const LAYOUT_KEY = 'mf_disparos_layout';

// ============ Styled ============

const PageHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 32px;
    font-weight: 500;
    margin: 0 0 4px;
    color: ${p => p.theme.ink};
  }
  p { color: ${p => p.theme.muted}; font-size: 13px; margin: 0; }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  color: ${p => p.theme.ink};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  outline: none;
  width: 220px;
  &:focus { border-color: ${p => p.theme.gold}; }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  overflow: hidden;
  background: ${p => p.theme.surface};
`;

const ViewToggleBtn = styled.button`
  border: none;
  background: ${p => p.$active ? p.theme.ink : 'transparent'};
  color: ${p => p.$active ? p.theme.bg : p.theme.inkSoft};
  padding: 7px 14px;
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 400};
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
`;

const Btn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.$primary ? p.theme.ink : p.theme.surface};
  color: ${p => p.$primary ? p.theme.bg : p.theme.ink};
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${p => p.$primary ? 600 : 500};
  cursor: pointer;
  font-family: inherit;
  transition: all .15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover:not(:disabled) {
    border-color: ${p => p.theme.gold};
    color: ${p => p.$primary ? p.theme.bg : p.theme.gold};
    transform: translateY(-1px);
  }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const DangerBtn = styled(Btn)`
  &:hover:not(:disabled) {
    border-color: #C5544A;
    color: #C5544A;
  }
`;

const DangerSolid = styled(Btn)`
  background: #9A3B3B;
  border-color: #9A3B3B;
  color: #fff;
  &:hover:not(:disabled) {
    color: #fff;
    border-color: #9A3B3B;
    box-shadow: 0 4px 14px rgba(154,59,59,.4);
  }
`;

// ===== Cards =====

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
`;

const Card = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 16px 18px;
  cursor: pointer;
  transition: transform .15s, border-color .15s, box-shadow .15s;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 130px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${p => p.theme.shadow};
    border-color: ${p => p.theme.gold};
  }
`;

const NewCard = styled(Card)`
  border: 1px dashed ${p => p.theme.line};
  background: transparent;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${p => p.theme.muted};
  &:hover { color: ${p => p.theme.gold}; transform: translateY(-2px); }
`;

const CardName = styled.h4`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.ink};
  line-height: 1.25;
  margin: 0;
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: ${p => p.theme.muted};
  letter-spacing: .3px;
`;

const CardStats = styled.div`
  margin-top: auto;
  display: flex;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid ${p => p.theme.line};
  font-size: 11px;
  color: ${p => p.theme.inkSoft};
  flex-wrap: wrap;
  b { color: ${p => p.theme.gold}; font-weight: 600; }
`;

const CardEmpty = styled.div`
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px dashed ${p => p.theme.lineDashed};
  font-size: 11px;
  color: ${p => p.theme.muted};
  font-style: italic;
`;

const Badge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 9px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 3px 7px;
  border-radius: 4px;
  font-weight: 600;
  background: ${p => p.$enviado ? p.theme.goldSoft : p.theme.surfaceAlt};
  color: ${p => p.$enviado ? p.theme.gold : p.theme.muted};
`;

// ===== Lista =====

const ListWrap = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 130px 70px 70px 70px 70px auto;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid ${p => p.theme.line};
  gap: 12px;
  cursor: pointer;
  transition: background .12s;
  &:hover { background: ${p => p.theme.surfaceAlt}; }
  &:last-child { border-bottom: none; }
`;

const ListHead = styled(ListRow)`
  background: ${p => p.theme.bg};
  cursor: default;
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  font-weight: 500;
  &:hover { background: ${p => p.theme.bg}; }
`;

const ListName = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.ink};
`;

const ListNum = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: ${p => p.theme.ink};
  &[data-empty="true"] { color: ${p => p.theme.muted}; font-style: italic; font-size: 11px; }
`;

const ListActions = styled.div`
  display: flex;
  gap: 4px;
  justify-content: end;
  button {
    background: transparent;
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.inkSoft};
    border-radius: 4px;
    width: 26px;
    height: 26px;
    cursor: pointer;
    font-size: 12px;
    &:hover { color: ${p => p.theme.gold}; border-color: ${p => p.theme.gold}; }
    &.danger:hover { color: #C5544A; border-color: #C5544A; }
  }
`;

// ===== Detalhe =====

const Breadcrumb = styled.div`
  margin-bottom: 14px;
  font-size: 12px;
  color: ${p => p.theme.muted};
  letter-spacing: .3px;
  button {
    background: transparent;
    border: none;
    color: ${p => p.theme.gold};
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    padding: 0;
    &:hover { text-decoration: underline; }
  }
`;

const DetailHead = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-left: 3px solid ${p => p.theme.gold};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 6px;
    color: ${p => p.theme.ink};
  }
  .sub { font-size: 12px; color: ${p => p.theme.muted}; }
  .head-btns { display: flex; gap: 8px; flex-wrap: wrap; }
`;

const StatsWrap = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 20px;
`;

const StatsHeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 8px;
  h3 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 19px;
    font-weight: 600;
    margin: 0;
    color: ${p => p.theme.ink};
  }
  .meta {
    font-size: 11px;
    color: ${p => p.theme.muted};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
`;

const StatCard = styled.div`
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  padding: 12px 14px;
  .lab {
    font-size: 9px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    font-weight: 500;
  }
  .val {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 26px;
    font-weight: 600;
    color: ${p => p.theme.ink};
    margin-top: 4px;
    line-height: 1;
  }
  .rate {
    font-size: 11px;
    color: ${p => p.theme.gold};
    margin-top: 2px;
  }
`;

const BouncesBox = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed ${p => p.theme.lineDashed};
  font-size: 12px;
  .lab {
    font-size: 9px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    font-weight: 500;
    margin-bottom: 6px;
  }
  ul { margin: 0; padding: 0; list-style: none; max-height: 140px; overflow-y: auto; }
  li {
    padding: 4px 0;
    border-bottom: 1px solid ${p => p.theme.line};
    font-family: ui-monospace, monospace;
    font-size: 11.5px;
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }
  li:last-child { border-bottom: none; }
  .reason { color: ${p => p.theme.muted}; font-style: italic; }
`;

const RunsBox = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed ${p => p.theme.lineDashed};
  .lab {
    font-size: 9px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    font-weight: 500;
    margin-bottom: 8px;
  }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td {
    text-align: left;
    padding: 7px 8px;
    border-bottom: 1px solid ${p => p.theme.line};
  }
  th {
    font-size: 9px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    font-weight: 500;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

// ===== Editor =====

const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Panel = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PanelTitle = styled.h3`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 19px;
  font-weight: 600;
  margin: 0;
  color: ${p => p.theme.ink};
`;

const Hint = styled.p`
  font-size: 11px;
  color: ${p => p.theme.muted};
  margin: 0 0 8px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  font-weight: 500;
  input, textarea {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 6px;
    padding: 9px 11px;
    font-size: 13px;
    font-family: inherit;
    letter-spacing: 0;
    text-transform: none;
    font-weight: 400;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
  textarea { resize: vertical; }
`;

const HtmlArea = styled.textarea`
  min-height: 360px;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace !important;
  font-size: 12.5px !important;
  line-height: 1.5;
`;

const InlineBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Counter = styled.div`
  font-size: 12px;
  color: ${p => p.theme.muted};
  display: flex;
  justify-content: space-between;
`;

const RecipientsBox = styled.div`
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  color: ${p => p.theme.inkSoft};
  max-height: 140px;
  overflow-y: auto;
  font-family: ui-monospace, monospace;
  line-height: 1.5;
  word-break: break-all;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 520px;
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  background: #fff;
`;

const Status = styled.div`
  font-size: 12px;
  padding: 9px 12px;
  border-radius: 6px;
  background: ${p => p.$err ? 'rgba(154,59,59,.10)' : p.theme.surfaceAlt};
  color: ${p => p.$err ? '#C5544A' : p.theme.inkSoft};
  border: 1px solid ${p => p.$err ? '#C5544A' : p.theme.line};
  white-space: pre-wrap;
  word-break: break-word;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 6px;
`;

const EmptyState = styled.div`
  padding: 60px 0;
  text-align: center;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

// ============ Helpers ============

function parseRecipients(raw) {
  if (!raw) return [];
  const tokens = raw
    .split(/[\s,;]+/)
    .map(s => s.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    const lower = t.toLowerCase();
    if (!EMAIL_RE.test(lower)) continue;
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(lower);
  }
  return out;
}

function buildImgTag(url) {
  return `<img src="${url}" alt="" style="display:block;max-width:600px;width:100%;height:auto;border:0;" />`;
}

function formatRelDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ============ Component ============

export default function EmailBlastPanel({ theme, admin }) {
  const isDev = !!admin?.is_dev;
  // navigation
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [selectedId, setSelectedId] = useState(null);

  // data
  const [templates, setTemplates] = useState([]);
  const [allSends, setAllSends] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [detailSends, setDetailSends] = useState([]);
  const [loadingDetailSends, setLoadingDetailSends] = useState(false);

  // list ui
  const [search, setSearch] = useState('');
  const [layout, setLayout] = useState(() => {
    if (typeof window === 'undefined') return 'cards';
    return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'cards';
  });
  const setLayoutSaved = (v) => {
    setLayout(v);
    if (typeof window !== 'undefined') localStorage.setItem(LAYOUT_KEY, v);
  };

  // form (detail view)
  const [subject, setSubject] = useState('');
  const [fromAddr, setFromAddr] = useState(DEFAULT_FROM);
  const [replyTo, setReplyTo] = useState('');
  const [html, setHtml] = useState('');
  const [csvRaw, setCsvRaw] = useState('');

  // ui state
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusErr, setStatusErr] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const htmlRef = useRef(null);
  const imgInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const selectedTpl = useMemo(
    () => templates.find(t => t.id === selectedId) || null,
    [templates, selectedId]
  );
  const recipients = useMemo(() => parseRecipients(csvRaw), [csvRaw]);
  const detailAggregate = useMemo(() => aggregateStats(detailSends), [detailSends]);
  const detailRuns = useMemo(() => groupByRun(detailSends), [detailSends]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t =>
      (t.nome || '').toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  // ===== data loading =====
  const refreshAll = async () => {
    const [tplRes, sendsRes] = await Promise.all([
      listTemplates(),
      listAllSendsWithTemplate(),
    ]);
    if (tplRes.ok) setTemplates(tplRes.items);
    if (sendsRes.ok) {
      setAllSends(sendsRes.items);
      setStatsMap(statsByTemplate(sendsRes.items));
    }
  };

  const refreshDetailSends = async (tplId) => {
    if (!tplId) { setDetailSends([]); return; }
    setLoadingDetailSends(true);
    const res = await listSendsByTemplate(tplId);
    setLoadingDetailSends(false);
    if (res.ok) setDetailSends(res.items);
  };

  useEffect(() => { refreshAll(); }, []);
  useEffect(() => {
    if (view === 'detail' && selectedId) refreshDetailSends(selectedId);
  }, [view, selectedId]);

  const setMsg = (text, isErr = false) => { setStatus(text); setStatusErr(isErr); };

  // ===== navigation =====
  const openTemplate = (tpl) => {
    if (tpl) {
      setSubject(tpl.subject || '');
      setHtml(tpl.html || '');
      setFromAddr(tpl.from_addr || DEFAULT_FROM);
      setReplyTo(tpl.reply_to || '');
      setSelectedId(tpl.id);
    } else {
      // novo rascunho
      setSubject('');
      setHtml('');
      setFromAddr(DEFAULT_FROM);
      setReplyTo('');
      setSelectedId(null);
    }
    setCsvRaw('');
    setStatus('');
    setStatusErr(false);
    setView('detail');
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToList = () => {
    setView('list');
    refreshAll();
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== template CRUD =====
  const saveCurrent = async () => {
    if (!selectedId) {
      // sem template = novo
      const nome = window.prompt('Nome do template:', subject?.slice(0, 60) || 'Sem nome');
      if (!nome || !nome.trim()) return;
      setBusy(true);
      const res = await createTemplate({
        nome: nome.trim(), subject, html, from_addr: fromAddr, reply_to: replyTo || null,
        created_by: admin?.email || null,
      });
      setBusy(false);
      if (!res.ok) { setMsg(`Erro ao salvar: ${res.error}`, true); return; }
      setSelectedId(res.item.id);
      await refreshAll();
      setMsg(`Template "${res.item.nome}" criado.`);
      return;
    }
    setBusy(true);
    const res = await updateTemplate(selectedId, {
      subject, html, from_addr: fromAddr, reply_to: replyTo || null,
    });
    setBusy(false);
    if (!res.ok) { setMsg(`Erro ao salvar: ${res.error}`, true); return; }
    await refreshAll();
    setMsg('Alterações salvas.');
  };

  const saveAsNew = async () => {
    const nome = window.prompt('Nome do novo template:', subject?.slice(0, 60) || '');
    if (!nome || !nome.trim()) return;
    setBusy(true);
    const res = await createTemplate({
      nome: nome.trim(), subject, html, from_addr: fromAddr, reply_to: replyTo || null,
      created_by: admin?.email || null,
    });
    setBusy(false);
    if (!res.ok) { setMsg(`Erro: ${res.error}`, true); return; }
    setSelectedId(res.item.id);
    await refreshAll();
    setMsg(`Novo template criado: "${res.item.nome}"`);
  };

  const renameTemplate = async (tplOrId) => {
    const tpl = typeof tplOrId === 'string'
      ? templates.find(t => t.id === tplOrId)
      : tplOrId;
    if (!tpl) return;
    const novoNome = window.prompt('Novo nome:', tpl.nome);
    if (!novoNome || !novoNome.trim() || novoNome.trim() === tpl.nome) return;
    const res = await updateTemplate(tpl.id, { nome: novoNome.trim() });
    if (!res.ok) { setMsg(`Erro: ${res.error}`, true); return; }
    await refreshAll();
    setMsg(`Renomeado: "${res.item.nome}"`);
  };

  const removeTemplate = async (tplOrId) => {
    const tpl = typeof tplOrId === 'string'
      ? templates.find(t => t.id === tplOrId)
      : tplOrId;
    if (!tpl) return;
    if (!window.confirm(`Excluir "${tpl.nome}"? Os disparos já feitos ficam preservados nos logs.`)) return;
    const res = await deleteTemplate(tpl.id);
    if (!res.ok) { setMsg(`Erro: ${res.error}`, true); return; }
    if (selectedId === tpl.id) backToList();
    else refreshAll();
    setMsg('Template excluído.');
  };

  // ===== uploads =====
  const handleInsertImageClick = () => imgInputRef.current?.click();

  const handleImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMsg('Selecione um arquivo de imagem.', true);
      return;
    }
    setUploading(true);
    setMsg('Enviando imagem...');
    try {
      const ts = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${STORAGE_FOLDER}/${ts}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, file, {
          cacheControl: '31536000',
          upsert: false,
          contentType: file.type,
        });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      const url = pub?.publicUrl || '';
      if (!url) throw new Error('Sem publicUrl');

      const ta = htmlRef.current;
      const tag = buildImgTag(url);
      if (ta) {
        const start = ta.selectionStart ?? html.length;
        const end = ta.selectionEnd ?? html.length;
        const next = html.slice(0, start) + tag + html.slice(end);
        setHtml(next);
        requestAnimationFrame(() => {
          ta.focus();
          const pos = start + tag.length;
          ta.setSelectionRange(pos, pos);
        });
      } else {
        setHtml(h => h + tag);
      }
      setMsg(`Imagem inserida: ${url}`);
    } catch (err) {
      setMsg(`Falha no upload: ${err.message || err}`, true);
    } finally {
      setUploading(false);
    }
  };

  const handleCsvSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setCsvRaw(text);
      const parsed = parseRecipients(text);
      setMsg(`${parsed.length} email(s) válido(s) carregado(s) do CSV.`);
    } catch (err) {
      setMsg(`Falha ao ler CSV: ${err.message || err}`, true);
    }
  };

  const refreshPreview = () => setPreviewKey(k => k + 1);

  // ===== disparo =====
  const callBlast = async (toList, label) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error('Sessão expirada. Faça login novamente.');

    const res = await fetch(BLAST_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        subject, html, from: fromAddr,
        replyTo: replyTo || undefined,
        recipients: toList,
        requestedBy: admin?.email || null,
        template_id: selectedId || null,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.message || `HTTP ${res.status}`);
    }
    setMsg(`${label} disparado. Run: ${data.runId || '(sem id)'}\nDestinatários: ${toList.length}`);

    if (selectedId && label === 'Disparo') {
      markTemplateSent(selectedId, data.runId, toList.length)
        .then(() => { refreshAll(); refreshDetailSends(selectedId); })
        .catch(() => {});
    }
  };

  const validateBeforeSend = () => {
    if (!subject.trim()) return 'Informe o assunto.';
    if (!fromAddr.trim()) return 'Informe o remetente.';
    if (!html.trim()) return 'Cole o HTML do email.';
    return null;
  };

  const handleSendTest = async () => {
    const err = validateBeforeSend();
    if (err) { setMsg(err, true); return; }
    const myEmail = admin?.email;
    if (!myEmail) { setMsg('Sem email do admin logado.', true); return; }
    setBusy(true);
    try { await callBlast([myEmail], 'Teste'); }
    catch (e) { setMsg(`Erro no teste: ${e.message || e}`, true); }
    finally { setBusy(false); }
  };

  const handleSendBlast = async () => {
    const err = validateBeforeSend();
    if (err) { setMsg(err, true); return; }
    if (recipients.length === 0) { setMsg('Carregue um CSV com pelo menos 1 email.', true); return; }
    if (!window.confirm(`Disparar para ${recipients.length} destinatário(s)?`)) return;
    if (!window.confirm(`Confirma o disparo? Esta ação não pode ser desfeita.`)) return;
    setBusy(true);
    try { await callBlast(recipients, 'Disparo'); }
    catch (e) { setMsg(`Erro no disparo: ${e.message || e}`, true); }
    finally { setBusy(false); }
  };

  // ============ RENDER ============

  if (view === 'list') {
    const totalEnviados = templates.filter(t => t.ultimo_envio_at).length;
    return (
      <>
        <PageHead>
          <div>
            <h1>Templates de Email</h1>
            <p>{templates.length} template(s) · {totalEnviados} disparado(s)</p>
          </div>
          <Toolbar>
            <SearchInput
              type="search"
              placeholder="Buscar template..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <ViewToggle>
              <ViewToggleBtn $active={layout === 'cards'} onClick={() => setLayoutSaved('cards')}>
                Cards
              </ViewToggleBtn>
              <ViewToggleBtn $active={layout === 'list'} onClick={() => setLayoutSaved('list')}>
                Lista
              </ViewToggleBtn>
            </ViewToggle>
            <Btn $primary onClick={() => openTemplate(null)}>+ Novo template</Btn>
          </Toolbar>
        </PageHead>

        {filteredTemplates.length === 0 && templates.length === 0 && (
          <EmptyState>
            Sem templates ainda. Clique em "+ Novo template" pra criar o primeiro.
          </EmptyState>
        )}
        {filteredTemplates.length === 0 && templates.length > 0 && (
          <EmptyState>Nenhum template casa com "{search}".</EmptyState>
        )}

        {filteredTemplates.length > 0 && layout === 'cards' && (
          <Grid>
            {filteredTemplates.map(t => {
              const st = statsMap[t.id];
              const enviado = !!t.ultimo_envio_at;
              return (
                <Card key={t.id} onClick={() => openTemplate(t)}>
                  <Badge $enviado={enviado}>{enviado ? 'enviado' : 'rascunho'}</Badge>
                  <CardName>{t.nome}</CardName>
                  <CardMeta>
                    {enviado
                      ? `Último envio: ${formatRelDate(t.ultimo_envio_at)}`
                      : `Criado em ${formatRelDate(t.created_at)}`}
                  </CardMeta>
                  {st && st.total > 0 ? (
                    <CardStats>
                      <span><b>{st.total}</b> envios</span>
                      <span><b>{st.rates.delivered}%</b> ✓</span>
                      <span><b>{st.rates.opened}%</b> 👁</span>
                      <span><b>{st.rates.clicked}%</b> 🔗</span>
                    </CardStats>
                  ) : (
                    <CardEmpty>{enviado ? 'Sem dados de tracking' : 'Ainda não disparado'}</CardEmpty>
                  )}
                </Card>
              );
            })}
            <NewCard onClick={() => openTemplate(null)}>
              <div style={{ fontSize: 28, lineHeight: 1 }}>+</div>
              <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', marginTop: 6 }}>
                Novo template
              </div>
            </NewCard>
          </Grid>
        )}

        {filteredTemplates.length > 0 && layout === 'list' && (
          <ListWrap>
            <ListHead>
              <div>Nome</div>
              <div>Último envio</div>
              <div style={{ textAlign: 'right' }}>Envios</div>
              <div style={{ textAlign: 'right' }}>Entr.</div>
              <div style={{ textAlign: 'right' }}>Abertos</div>
              <div style={{ textAlign: 'right' }}>Cliques</div>
              <div></div>
            </ListHead>
            {filteredTemplates.map(t => {
              const st = statsMap[t.id];
              return (
                <ListRow key={t.id} onClick={() => openTemplate(t)}>
                  <ListName>{t.nome}</ListName>
                  <div style={{ fontSize: 12, color: theme.muted }}>
                    {t.ultimo_envio_at ? formatRelDate(t.ultimo_envio_at) : '—'}
                  </div>
                  {st && st.total > 0 ? (
                    <>
                      <ListNum>{st.total}</ListNum>
                      <ListNum>{st.rates.delivered}%</ListNum>
                      <ListNum>{st.rates.opened}%</ListNum>
                      <ListNum>{st.rates.clicked}%</ListNum>
                    </>
                  ) : (
                    <>
                      <ListNum data-empty="true">{t.ultimo_envio_at ? 's/dados' : 'rascunho'}</ListNum>
                      <ListNum>—</ListNum>
                      <ListNum>—</ListNum>
                      <ListNum>—</ListNum>
                    </>
                  )}
                  <ListActions onClick={e => e.stopPropagation()}>
                    <button title="Renomear" onClick={() => renameTemplate(t)}>✎</button>
                    <button className="danger" title="Excluir" onClick={() => removeTemplate(t)}>×</button>
                  </ListActions>
                </ListRow>
              );
            })}
          </ListWrap>
        )}
      </>
    );
  }

  // ===== DETAIL VIEW =====
  const isNovo = !selectedId;
  return (
    <>
      <Breadcrumb>
        <button onClick={backToList}>← Voltar para Templates</button>
      </Breadcrumb>

      <DetailHead>
        <div>
          <h1>{isNovo ? 'Novo template' : selectedTpl?.nome || 'Template'}</h1>
          <div className="sub">
            {isNovo
              ? 'Preencha os campos e salve para criar o template.'
              : `Editado em ${formatRelDate(selectedTpl?.updated_at)}${selectedTpl?.created_by ? ` · por ${selectedTpl.created_by}` : ''}`}
          </div>
        </div>
        {!isNovo && (
          <div className="head-btns">
            <Btn onClick={() => renameTemplate(selectedTpl)}>Renomear</Btn>
            <DangerBtn onClick={() => removeTemplate(selectedTpl)}>Excluir</DangerBtn>
          </div>
        )}
      </DetailHead>

      {!isNovo && (
        <StatsWrap>
          <StatsHeadRow>
            <h3>Métricas</h3>
            <span className="meta">
              {loadingDetailSends
                ? 'Carregando…'
                : `${detailAggregate.total} envio(s) · ${detailAggregate.runs} disparo(s)`}
              {' '}
              <Btn onClick={() => refreshDetailSends(selectedId)} style={{ padding: '3px 8px', fontSize: 10, marginLeft: 6 }}>
                ↻ Atualizar
              </Btn>
            </span>
          </StatsHeadRow>

          {detailAggregate.total === 0 ? (
            <Hint style={{ fontStyle: 'italic' }}>
              Ainda sem disparos rastreados deste template.
            </Hint>
          ) : (
            <>
              <StatsGrid>
                <StatCard>
                  <div className="lab">Enviados</div>
                  <div className="val">{detailAggregate.total}</div>
                </StatCard>
                <StatCard>
                  <div className="lab">Entregues</div>
                  <div className="val">{detailAggregate.delivered}</div>
                  <div className="rate">{detailAggregate.rates.delivered}%</div>
                </StatCard>
                <StatCard>
                  <div className="lab">Abertos (únicos)</div>
                  <div className="val">{detailAggregate.opened}</div>
                  <div className="rate">{detailAggregate.rates.opened}%</div>
                </StatCard>
                <StatCard>
                  <div className="lab">Cliques (únicos)</div>
                  <div className="val">{detailAggregate.clicked}</div>
                  <div className="rate">{detailAggregate.rates.clicked}%</div>
                </StatCard>
                <StatCard>
                  <div className="lab">Bounces</div>
                  <div className="val">{detailAggregate.bounced}</div>
                  <div className="rate">{detailAggregate.rates.bounced}%</div>
                </StatCard>
                {(detailAggregate.complained > 0 || detailAggregate.failed > 0) && (
                  <StatCard>
                    <div className="lab">Spam / Falha</div>
                    <div className="val">{detailAggregate.complained + detailAggregate.failed}</div>
                  </StatCard>
                )}
              </StatsGrid>

              {detailAggregate.bounces.length > 0 && (
                <BouncesBox>
                  <div className="lab">Bounces ({detailAggregate.bounces.length})</div>
                  <ul>
                    {detailAggregate.bounces.slice(0, 30).map((b, i) => (
                      <li key={i}>
                        <span>{b.email}</span>
                        <span className="reason">{b.reason || '—'}</span>
                      </li>
                    ))}
                  </ul>
                </BouncesBox>
              )}

              {detailRuns.length > 1 && (
                <RunsBox>
                  <div className="lab">Histórico de disparos</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th className="num">Envios</th>
                        <th className="num">Entregues</th>
                        <th className="num">Abertos</th>
                        <th className="num">Cliques</th>
                        <th className="num">Bounces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRuns.slice(0, 10).map((r, i) => (
                        <tr key={r.run_id || i}>
                          <td>{formatRelDate(r.sent_at)}</td>
                          <td className="num">{r.stats.total}</td>
                          <td className="num">{r.stats.delivered}</td>
                          <td className="num">{r.stats.opened}</td>
                          <td className="num">{r.stats.clicked}</td>
                          <td className="num">{r.stats.bounced}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </RunsBox>
              )}
            </>
          )}
        </StatsWrap>
      )}

      <EditorGrid>
        <Panel>
          <Field>
            Assunto
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Ex.: Conheça Alphaville de um novo ângulo"
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field>
              Remetente
              <input
                type="text"
                value={fromAddr}
                onChange={e => setFromAddr(e.target.value)}
                placeholder='Nome <email@dominio.com>'
              />
            </Field>
            <Field>
              Responder para (opcional)
              <input
                type="email"
                value={replyTo}
                onChange={e => setReplyTo(e.target.value)}
                placeholder="contato@..."
              />
            </Field>
          </div>

          <Field>
            HTML do email
            <InlineBar>
              <Btn type="button" onClick={handleInsertImageClick} disabled={uploading}>
                {uploading ? 'Enviando...' : '+ Inserir imagem'}
              </Btn>
              <Btn type="button" onClick={refreshPreview}>Atualizar preview</Btn>
              <input
                ref={imgInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelected}
                style={{ display: 'none' }}
              />
            </InlineBar>
            <HtmlArea
              ref={htmlRef}
              value={html}
              onChange={e => setHtml(e.target.value)}
              placeholder="<!DOCTYPE html>..."
              spellCheck={false}
            />
            <Counter>
              <span>{html.length.toLocaleString('pt-BR')} caracteres</span>
            </Counter>
          </Field>

          <Field>
            Destinatários (CSV)
            <InlineBar>
              <Btn type="button" onClick={() => csvInputRef.current?.click()}>
                Carregar CSV
              </Btn>
              <Btn type="button" onClick={() => setCsvRaw('')} disabled={!csvRaw}>
                Limpar
              </Btn>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={handleCsvSelected}
                style={{ display: 'none' }}
              />
            </InlineBar>
            <Counter>
              <span>{recipients.length} válido(s)</span>
              <span style={{ opacity: .6 }}>duplicados/ inválidos removidos</span>
            </Counter>
            {recipients.length > 0 && (
              <RecipientsBox>
                {recipients.slice(0, 50).join(', ')}
                {recipients.length > 50 && ` … (+${recipients.length - 50})`}
              </RecipientsBox>
            )}
          </Field>

          {status && <Status $err={statusErr}>{status}</Status>}

          <ActionBar>
            <Btn onClick={saveCurrent} disabled={busy}>
              {isNovo ? 'Salvar template' : 'Salvar alterações'}
            </Btn>
            {!isNovo && (
              <Btn onClick={saveAsNew} disabled={busy}>Salvar como novo</Btn>
            )}
            <Btn onClick={handleSendTest} disabled={busy}>Enviar teste pra mim</Btn>
            {isDev && (
              <DangerSolid onClick={handleSendBlast} disabled={busy || recipients.length === 0}>
                {busy ? 'Enviando...' : `Disparar (${recipients.length})`}
              </DangerSolid>
            )}
          </ActionBar>
        </Panel>

        <Panel>
          <PanelTitle>Pré-visualização</PanelTitle>
          <Hint>Renderiza o HTML em sandbox. Atualize após editar.</Hint>
          <PreviewFrame
            key={previewKey}
            srcDoc={html || '<p style="font-family:sans-serif;color:#888;padding:24px">Sem HTML.</p>'}
            sandbox=""
            title="Preview do email"
          />
        </Panel>
      </EditorGrid>
    </>
  );
}
